/**
 * Registry for the corporate legal documents served on legal.meitre.com.
 * Tenant-agnostic (neutral Meitre brand) — see LegalLayout.
 */

export type LegalDocKey = 'privacy-policy' | 'terms-and-conditions' | 'terms-service';

/** Canonical doc slugs, in display order. Slug === legal.meitre.com/{slug}. */
export const LEGAL_DOCS: readonly LegalDocKey[] = [
  'privacy-policy',
  'terms-and-conditions',
  'terms-service',
];

/**
 * Languages offered on the legal pages. EN + ES are ported from the legacy
 * marketing site; PT/FR/DE/IT are translations served under an "English version
 * governs" convenience notice (see LegalDocPage). IT is unique to these pages.
 */
export const LEGAL_LANGS = ['en', 'es', 'pt', 'fr', 'de', 'it'] as const;
export type LegalLang = (typeof LEGAL_LANGS)[number];

export const isLegalLang = (value: string | undefined): value is LegalLang =>
  !!value && (LEGAL_LANGS as readonly string[]).includes(value);

/**
 * Docs that exist as an *authoritative* (non-English) original, NOT a
 * convenience translation. The legacy site shipped Spanish Privacy Policy and
 * Terms & Conditions that are materially different from the English (LatAm-
 * specific clauses) — they govern in their own right, so they must NOT carry the
 * "English version governs" notice. Everything else non-English is a convenience
 * translation of the English source.
 */
const AUTHORITATIVE_ORIGINALS: Partial<Record<LegalLang, readonly LegalDocKey[]>> = {
  es: ['privacy-policy', 'terms-and-conditions'],
};

export const isConvenienceTranslation = (doc: LegalDocKey, lang: string): boolean => {
  if (lang === 'en') return false;
  return !AUTHORITATIVE_ORIGINALS[lang as LegalLang]?.includes(doc);
};

/** Build a canonical path for a doc in a language, e.g. /privacy-policy/es. */
export const legalPath = (doc: LegalDocKey, lang?: string): string =>
  lang && lang !== 'en' ? `/${doc}/${lang}` : `/${doc}`;

/**
 * Resolve the active language from the URL `:lang` param. The bare `/{doc}`
 * route is the canonical ENGLISH page — so the `EN` selector (which links to the
 * bare URL) and the apex redirects are deterministic, and the bare URL always
 * serves the authoritative English source. The consumer app links to
 * `/{doc}/{lang}` to open a doc in a specific (non-English) language.
 */
export const resolveLegalLang = (urlLang: string | undefined): LegalLang =>
  isLegalLang(urlLang) ? urlLang : 'en';
